import type { User } from '@/modules/Auth/userTypes';

import { decodeJwtPayload } from './jwtPayload';

type ClerkUserLike = {
  id: string;
  username: string | null;
  firstName: string | null;
  lastName: string | null;
  primaryEmailAddress?: { emailAddress: string; verification?: { status?: string } } | null;
};

function claim(payload: Record<string, unknown>, camel: string, snake: string): string {
  const a = payload[camel];
  const b = payload[snake];
  if (a !== undefined && a !== null && String(a) !== '') return String(a);
  if (b !== undefined && b !== null && String(b) !== '') return String(b);
  return '';
}

const emptyPool = {
  userPoolId: '',
  clientId: '',
  client: { endpoint: '', fetchOptions: {} },
  advancedSecurityDataCollectionFlag: false,
  storage: { domain: '', path: '/', expires: 365, secure: false, sameSite: 'lax' },
};

/** Map Clerk session JWT + user into the Cognito-shaped User used across dashboard hooks. */
export function buildUserFromClerkSession(jwt: string, clerkUser: ClerkUserLike): User | null {
  const token = jwt.trim();
  if (!token) {
    return null;
  }

  const payload = decodeJwtPayload(token);
  if (!payload) {
    return null;
  }

  const sub = String(payload.sub || clerkUser.id || '');
  const email =
    clerkUser.primaryEmailAddress?.emailAddress ||
    String(payload.email || clerkUser.username || sub);
  const givenName = clerkUser.firstName || String(payload.given_name || 'User');
  const familyName = clerkUser.lastName || String(payload.family_name || '.');
  const orgRole = String(payload.org_role || '');
  const groups = orgRole === 'admin' || orgRole === 'org:admin' ? ['admin'] : [];
  const tenantId = claim(payload, 'custom:tenantId', 'custom:tenant_id');
  const userId = claim(payload, 'custom:userId', 'custom:user_id') || sub;
  const userRole = claim(payload, 'custom:userRole', 'custom:user_role') || (groups[0] ?? 'member');
  const now = Math.floor(Date.now() / 1000);

  const idPayload = {
    sub,
    'cognito:groups': groups,
    email_verified: clerkUser.primaryEmailAddress?.verification?.status === 'verified',
    iss: String(payload.iss || 'clerk'),
    'cognito:username': email,
    given_name: givenName,
    'custom:userId': userId,
    origin_jti: '',
    'custom:tenantId': tenantId,
    aud: String(payload.azp || payload.aud || ''),
    event_id: '',
    'custom:userRole': userRole,
    token_use: 'id',
    auth_time: Number(payload.iat || now),
    exp: Number(payload.exp || now + 3600),
    iat: Number(payload.iat || now),
    family_name: familyName,
    jti: '',
    email,
  };

  const accessPayload = {
    sub,
    'cognito:groups': groups,
    iss: String(payload.iss || 'clerk'),
    client_id: String(payload.azp || ''),
    origin_jti: '',
    event_id: '',
    token_use: 'access',
    scope: '',
    auth_time: Number(payload.iat || now),
    exp: Number(payload.exp || now + 3600),
    iat: Number(payload.iat || now),
    jti: '',
    'custom:userRole': userRole,
    username: email,
  };

  return {
    username: email,
    pool: emptyPool,
    Session: null,
    client: { endpoint: '', fetchOptions: {} },
    signInUserSession: {
      idToken: { jwtToken: token, payload: idPayload },
      refreshToken: { token: '' },
      accessToken: { jwtToken: token, payload: accessPayload },
      clockDrift: 0,
    },
    attributes: {
      email,
      email_verified: idPayload.email_verified,
      family_name: familyName,
      given_name: givenName,
      sub,
      custom: {
        tenantId,
        userId,
        userRole,
      },
    },
  };
}
